/**
 * Deterministic transaction classification. Ported from v28 with two fixes:
 *  - "money market" is matched before "market", so investments are no longer
 *    filed as groceries;
 *  - "land" matches the whole word only, so "Island" is not property.
 * Material rows the rules cannot place, and material rows whose amount was
 * corrected from the running balance, go to Review instead of being guessed.
 */
import type { ParsedTransaction } from './parser/types';
import type { Category, CategoryType } from '../data/types';
import { findPayeeRule, type PayeeRule } from './payee';

export const MATERIAL = { NGN: 100_000, USD: 100 } as const;

interface Rule {
  test: RegExp;
  /** Category names in order of preference; the first one that exists is used, the first is created if none does. */
  name: string | string[];
  /** Category type to create if no category with this name exists. Only for non-spending capital buckets. */
  systemType?: CategoryType;
  confidence: number;
  internal?: boolean;
  business?: boolean;
  reimbursement?: boolean;
  /** Only applies to money in, or only to money out. */
  direction?: 'debit' | 'credit';
  /** Always ask the owner when material (e.g. salary or reimbursement?). */
  review?: string;
}

/**
 * Owner's own-account transfers. The owner's name must be the RECIPIENT: banks print
 * the sender's name too ("FROM NEDD,LYNDELL AYODELE TO someone"), which is not internal.
 */
const OWN_NAME = String.raw`(?:nedd[ ,]+lyndell|lyndell (?:ayodele )?nedd)`;
const OWN_ACCOUNT = new RegExp(String.raw`own account|spend to save|savings from expenses|\bto\b[^/]*?\b${OWN_NAME}`);

export const RULES: Rule[] = [
  // Cash first: cash withdrawals also print the owner's name.
  { test: /cash ?withdrawal|cash ?out|\batm\b/, name: 'Cash', systemType: 'other', confidence: 0.97 },
  { test: OWN_ACCOUNT, name: 'Internal Transfer', systemType: 'transfer', confidence: 0.99, internal: true },
  { test: /migrate world|business capital|business funding|mw recruitment|recruit drive|\bmw\b/, name: ['Migrate World', 'Business Capital'], systemType: 'business', confidence: 0.98, business: true },
  { test: /property|real estate|\bland\b|mortgage|gratia height/, name: 'Property', systemType: 'property', confidence: 0.96 },
  { test: /reimburse/, name: 'Reimbursement', confidence: 0.98, reimbursement: true },
  { test: /salary|payroll/, direction: 'credit', name: ['Salary', 'Income'], confidence: 0.97 },
  { test: /director.*allowance|seating allowance|sitting allowance|board fee/, direction: 'credit', name: ['Other Earned Income', 'Income'], confidence: 0.95 },
  // Salary arrives from Descasio under a payee rule with an amount band; any other Descasio credit is asked about.
  { test: /descasio/, direction: 'credit', name: ['Business Reimbursement', 'Reimbursement', 'Other Inflow'], confidence: 0.6, reimbursement: true, review: 'Money from Descasio: salary or a reimbursement?' },
  { test: /investment|money market|money mkt|united capital|mutual fund|treasury|fixed deposit|\bmmf\b|\bfd\b|\barm\b/, name: 'Investments', confidence: 0.94 },
  { test: /airline|flight|dragonpass|turkish|\bthy\b|duty ?free/, name: 'Travel', confidence: 0.94 },
  { test: /uber|bolt|lagride|transport|fuel|petrol|diesel|filling station|service station/, name: 'Transport', confidence: 0.94 },
  { test: /netflix|google play|apple\.com|subscription|subscr|dstv|multichoice|internet|airtime|data bundle|openai|chatgpt|spotify|youtube|prime video|freedom mobile|airtel|\bmtn\b/, name: 'Subscriptions & Digital', confidence: 0.94 },
  { test: /restaurant|bistro|\bcaf|coffee|roaster|dinner|lunch|hospitality|glovo|chowdeck|yogurt|boba|food/, name: 'Dining & Social', confidence: 0.93 },
  { test: /supermarket|grocery|groceries|\bmarket\b|pikwik|spar\b/, name: 'Food & Groceries', confidence: 0.93 },
  { test: /clinic|hospital|pharmacy|medical|health/, name: 'Health', confidence: 0.95 },
  { test: /hotel|booking|travel|visa|airbnb/, name: 'Travel', confidence: 0.92 },
  { test: /amazon|temu|jumia|konga|store|mall|outfitters|fashion|shopping/, name: 'Shopping', confidence: 0.9 },
  { test: /electric|power|utility|estate dues|service charge/, name: 'Home & Utilities', confidence: 0.92 },
  { test: /laundry|primewash|dry ?clean/, name: ['Services', 'Home & Utilities'], confidence: 0.92 },
  { test: /commission|vat|stamp duty|sms alert|sms charge|account maint|maintenance fee|charges/, name: ['Bank Fees', 'Other / Flex'], confidence: 0.96 },
];

/** Purpose words in a narration that override an everyday payee's category. */
const PURPOSE: { test: RegExp; name: string | string[] }[] = [
  { test: /\b(petrol|fuel|diesel)\b/, name: 'Transport' },
  { test: /\bwater\b/, name: 'Home & Utilities' },
];

const norm = (s: string) => s.toLowerCase().replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ').trim();

export function findCategory(categories: Category[], name: string | string[]): Category | null {
  for (const n of typeof name === 'string' ? [name] : name) {
    const target = norm(n);
    const hit = categories.find((c) => c.is_active !== false && norm(c.name) === target);
    if (hit) return hit;
  }
  return null;
}

const firstName = (name: string | string[]) => (typeof name === 'string' ? name : name[0]!);

export interface Classification {
  classification: string;
  category: Category | null;
  /** Set when the matching capital category does not exist yet and must be created first. */
  missingSystemCategory: { name: string; type: CategoryType } | null;
  confidence: number;
  is_internal_transfer: boolean;
  is_business_funding: boolean;
  is_reimbursement: boolean;
  needs_review: boolean;
  reviewReason: string | null;
}

export function classify(tx: ParsedTransaction, currency: string, categories: Category[], payeeRules: PayeeRule[] = []): Classification {
  const text = tx.description.toLowerCase();
  const material = tx.amount >= (currency === 'USD' ? MATERIAL.USD : MATERIAL.NGN);

  // The owner's own decisions come first.
  const saved = findPayeeRule(tx.description, payeeRules, { direction: tx.direction, amount: tx.amount });
  let savedCategory = saved ? categories.find((c) => c.id === saved.category_id) ?? null : null;
  // An errand narration says what the money was for ("PETROL", "WATER"): for an everyday
  // payee (a driver, a housekeeper) that purpose wins over the payee's usual category.
  if (savedCategory?.category_type === 'lifestyle') {
    const purpose = PURPOSE.find((p) => p.test.test(text));
    const target = purpose ? findCategory(categories, purpose.name) : null;
    if (target) savedCategory = target;
  }
  const corrected = material && (tx.amountSource === 'direction_corrected' || tx.amountSource === 'merged_cell_resolved');
  if (savedCategory) {
    const capital = savedCategory.category_type;
    return {
      classification: savedCategory.name,
      category: savedCategory,
      missingSystemCategory: null,
      confidence: 0.99,
      is_internal_transfer: capital === 'transfer',
      is_business_funding: capital === 'business',
      is_reimbursement: false,
      needs_review: corrected,
      reviewReason: corrected ? 'Amount was corrected from the running balance' : null,
    };
  }
  const rule = RULES.find((r) => (!r.direction || r.direction === tx.direction) && r.test.test(text));

  let wanted: string | string[];
  let confidence: number;
  if (rule) {
    wanted = rule.name;
    confidence = rule.confidence;
  } else if (tx.direction === 'credit') {
    // An unrecognised credit is not assumed to be earned income.
    wanted = ['Other Inflow', 'Income'];
    confidence = 0.85;
  } else {
    wanted = 'Other / Flex';
    confidence = material ? 0.55 : 0.9;
  }

  const category = findCategory(categories, wanted);
  const name = category?.name ?? firstName(wanted);
  const missingSystemCategory = !category && rule?.systemType ? { name: firstName(rule.name), type: rule.systemType } : null;

  let reviewReason: string | null = null;
  if (rule?.review && material) {
    reviewReason = rule.review;
  } else if (tx.direction === 'debit' && material && confidence < 0.8) {
    reviewReason = 'Material payment the rules could not place';
  } else if (material && (tx.amountSource === 'direction_corrected' || tx.amountSource === 'merged_cell_resolved')) {
    reviewReason = 'Amount was corrected from the running balance';
  } else if (tx.amountSource === 'unchecked' && material) {
    reviewReason = 'Row could not be checked against a running balance';
  }

  return {
    classification: name,
    category,
    missingSystemCategory,
    confidence,
    is_internal_transfer: Boolean(rule?.internal),
    is_business_funding: Boolean(rule?.business),
    is_reimbursement: Boolean(rule?.reimbursement),
    needs_review: reviewReason !== null,
    reviewReason,
  };
}
